import { Component, type ErrorInfo, type ReactNode } from 'react';
import { t, type Idioma } from '@matias-asset-os/i18n';

/**
 * UNA PANTALLA QUE FALLA NO SE QUEDA EN BLANCO.
 *
 * Cuando algo revienta al dibujar, React desmonta la aplicacion entera y deja
 * la pagina vacia. Quien lo sufre no tiene nada que contar —«se queda en
 * blanco»— y quien lo arregla no tiene nada con que empezar: hay que reproducir
 * a ciegas con datos que solo existen en produccion.
 *
 * Esto atrapa el fallo, deja el resto de la aplicacion en pie y ENSENA lo que
 * paso, con un boton para copiarlo. Un mensaje feo pero exacto vale mas que una
 * pantalla limpia que no dice nada.
 */

interface Props {
  idioma: Idioma;
  children: ReactNode;
  /** Para volver a un sitio seguro sin recargar. */
  onVolver?: () => void;
}

interface Estado {
  error: Error | null;
  donde: string | null;
}

export class PantallaRota extends Component<Props, Estado> {
  override state: Estado = { error: null, donde: null };

  static getDerivedStateFromError(error: Error): Partial<Estado> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Queda en la consola ademas de en pantalla: en el movil no hay consola,
    // pero en un ordenador es donde se mira primero.
    console.error('Pantalla rota:', error, info.componentStack);
    this.setState({ donde: info.componentStack ?? null });
  }

  private texto(): string {
    const { error, donde } = this.state;
    return [
      `${error?.name ?? 'Error'}: ${error?.message ?? ''}`,
      error?.stack ?? '',
      donde ?? '',
    ]
      .join('\n')
      .trim();
  }

  override render(): ReactNode {
    const { error } = this.state;
    const { idioma, children, onVolver } = this.props;
    if (!error) return children;

    return (
      <main>
        <section className="tarjeta pantalla-rota">
          <h1>{t(idioma, 'roto.titulo')}</h1>
          <p className="suave">{t(idioma, 'roto.explicacion')}</p>
          <pre className="roto-detalle">{this.texto()}</pre>
          <div className="fila-flex" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard?.writeText(this.texto()).catch(() => undefined);
              }}
            >
              {t(idioma, 'roto.copiar')}
            </button>
            {onVolver && (
              <button
                type="button"
                className="secundario"
                onClick={() => {
                  this.setState({ error: null, donde: null });
                  onVolver();
                }}
              >
                {t(idioma, 'roto.volver')}
              </button>
            )}
          </div>
        </section>
      </main>
    );
  }
}
